import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "../lib/api.ts";
import { OutlineButton } from "../components/FilterBar.tsx";
import { theme } from "../theme.ts";
import { LeadTimesCard } from "../components/LeadTimesCard.tsx";
import { SafetyCard } from "../components/SafetyCard.tsx";
import { NotStockedCard } from "../components/NotStockedCard.tsx";
import { AgentAccessCard } from "../components/AgentAccessCard.tsx";
import { PasswordLoginsCard } from "../components/PasswordLoginsCard.tsx";

interface Progress {
  ok: boolean;
  pct_complete: number | null;
  counts_by_status: Record<string, number>;
  total_shards: number;
}
interface IngestionRuns {
  runs: Array<{
    id: number;
    job_name: string;
    status: string;
    started_at: number;
    completed_at: number | null;
    rows_written: number | null;
    error_message: string | null;
    triggered_by: string | null;
  }>;
}
const JOBS = [
  "sku_catalog",
  "inventory_snapshot",
  "open_pos",
  "demand_backfill",
  "drain_backfill",
  "demand_current_month",
  "compute_forecast",
];

export function AdminPage() {
  const progress = useQuery<Progress>({
    queryKey: ["backfill-progress"],
    queryFn: () => apiGet<Progress>("/api/health/backfill_progress"),
    refetchInterval: 30_000,
  });
  const runs = useQuery<IngestionRuns>({
    queryKey: ["ingestion-runs"],
    queryFn: () => apiGet<IngestionRuns>("/api/ingest/runs"),
    refetchInterval: 30_000,
  });

  const triggerJob = async (job: string) => {
    try {
      await apiPost<{ ok: boolean }>(`/api/ingest/${job}`);
      alert(`Job "${job}" triggered.`);
      runs.refetch();
    } catch (e) {
      alert(`Failed: ${(e as Error).message}`);
    }
  };

  const pct = progress.data?.pct_complete ?? 0;

  return (
    <div style={{ display: "grid", gap: theme.space.xl }}>
      {/* Lead-time settings */}
      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: "0 0 12px",
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Lead times
        </h3>
        <LeadTimesCard />
      </section>

      {/* Safety buffer settings */}
      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: "0 0 12px",
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Safety buffer
        </h3>
        <SafetyCard />
      </section>

      {/* Parts Operations doesn't keep in stock */}
      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: "0 0 12px",
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Not stocked
        </h3>
        <NotStockedCard />
      </section>

      {/* Agent access: MCP, API keys, audit */}
      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: "0 0 12px",
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Agent access
        </h3>
        <AgentAccessCard />
      </section>

      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: "0 0 12px",
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Password logins
        </h3>
        <PasswordLoginsCard />
      </section>

      {/* Backfill progress card */}
      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Backfill Progress
        </h3>
        {progress.data && (
          <>
            <div
              style={{
                marginTop: theme.space.sm,
                fontSize: theme.font.size.xxl,
                fontWeight: theme.font.weight.bold,
                letterSpacing: -0.5,
              }}
            >
              {pct}%
              <span
                style={{
                  marginLeft: theme.space.sm,
                  fontSize: theme.font.size.md,
                  color: theme.color.textMuted,
                  fontWeight: theme.font.weight.regular,
                }}
              >
                {progress.data.counts_by_status.completed ?? 0} /{" "}
                {progress.data.total_shards} shards
              </span>
            </div>
            <div
              style={{
                marginTop: theme.space.md,
                background: theme.color.border,
                height: 10,
                borderRadius: 999,
                maxWidth: 480,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: theme.color.brand,
                  height: "100%",
                  width: `${pct}%`,
                  transition: "width 500ms ease",
                }}
              />
            </div>
            <div
              style={{
                marginTop: theme.space.md,
                display: "flex",
                gap: theme.space.md,
                flexWrap: "wrap",
                fontSize: theme.font.size.sm,
                color: theme.color.textMuted,
              }}
            >
              {Object.entries(progress.data.counts_by_status).map(([k, v]) => (
                <span key={k}>
                  {k}:{" "}
                  <b
                    style={{
                      color:
                        k === "completed"
                          ? theme.color.success
                          : k === "failed"
                            ? theme.color.danger
                            : theme.color.text,
                    }}
                  >
                    {v}
                  </b>
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Manual triggers card */}
      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Manual Triggers
        </h3>
        <p
          style={{
            marginTop: theme.space.sm,
            fontSize: theme.font.size.sm,
            color: theme.color.textMuted,
          }}
        >
          Jobs run asynchronously via <code>ctx.waitUntil</code>. Watch progress
          in the ingestion runs log below.
        </p>
        <div
          style={{
            display: "flex",
            gap: theme.space.sm,
            flexWrap: "wrap",
            marginTop: theme.space.md,
          }}
        >
          {JOBS.map((j) => (
            <OutlineButton key={j} onClick={() => triggerJob(j)}>
              {j}
            </OutlineButton>
          ))}
        </div>
      </section>

      {/* Ingestion runs card */}
      <section
        style={{
          background: theme.color.surface,
          border: `1px solid ${theme.color.border}`,
          borderRadius: theme.radius.lg,
          padding: theme.space.xl,
          boxShadow: theme.shadow.sm,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: theme.font.size.md,
            fontWeight: theme.font.weight.semibold,
            color: theme.color.textMuted,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: theme.space.md,
          }}
        >
          Recent Ingestion Runs
        </h3>
        <div style={{ overflow: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: theme.font.size.md,
            }}
          >
            <thead>
              <tr>
                {["Job", "Status", "Rows", "Elapsed", "Started", "Trigger"].map(
                  (h, i) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 12px",
                        textAlign: i === 2 || i === 3 ? "right" : "left",
                        borderBottom: `1px solid ${theme.color.border}`,
                        fontWeight: theme.font.weight.semibold,
                        color: theme.color.textMuted,
                        fontSize: theme.font.size.xs,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {(runs.data?.runs ?? []).slice(0, 30).map((r) => {
                const elapsed = r.completed_at
                  ? ((r.completed_at - r.started_at) / 1000).toFixed(1) + "s"
                  : "…";
                const statusColor =
                  r.status === "completed"
                    ? theme.color.success
                    : r.status === "failed"
                      ? theme.color.danger
                      : r.status === "partial"
                        ? theme.color.warning
                        : theme.color.textMuted;
                return (
                  <tr
                    key={r.id}
                    style={{ borderBottom: `1px solid ${theme.color.border}` }}
                  >
                    <td
                      style={{
                        padding: "6px 12px",
                        fontFamily: theme.font.mono,
                        fontSize: theme.font.size.sm,
                      }}
                    >
                      {r.job_name}
                    </td>
                    <td style={{ padding: "6px 12px", color: statusColor }}>
                      {r.status}
                    </td>
                    <td
                      style={{
                        padding: "6px 12px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {r.rows_written?.toLocaleString() ?? "—"}
                    </td>
                    <td
                      style={{
                        padding: "6px 12px",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {elapsed}
                    </td>
                    <td style={{ padding: "6px 12px", color: theme.color.textMuted }}>
                      {new Date(r.started_at).toLocaleString()}
                    </td>
                    <td style={{ padding: "6px 12px", color: theme.color.textMuted }}>
                      {r.triggered_by ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
